<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { buildChangeAddressVariables, type GoverningDocumentType } from '../mappers/changeAddressMapper';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import PreSetDateField from './PreSetDateField.vue';

const MALAYSIA_CODE = 'B15135';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + create→update 분기.
const editMode = computed(() => !!props.editTaskId);
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true;
  if (editBaseline.value == null) return false;
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'create-task', data: { taskTitle: string }): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

const router = useRouter();

const creating = ref(false);
const progress = ref('');

// 진행 상태는 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다.
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

// Task config processSteps (Document / T-Sign / e-Filing / Register 플래그 유도)
const taskConfigSteps = ref<string[]>([]);

// Task Title 은 chat 단계에서 입력되어 props.taskTitle 로 들어옴
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());

// ─── 구 주소 / 정관번호 (backend 에서 composed+structured 로 받음) ───
const addressInfoLoading = ref(false);
const regNo = ref('');
const articleNo = ref('');   // 전체 텍스트 (e.g. "Article 106 of the Constitution ...")
const clauseNo = ref('');    // 숫자만 추출 (e.g. "106")
const oldAddressComposed = ref('');
const oldAddressStructured = ref<Record<string, any>>({});
// DCR 조건문용 — [[if isSoleDirector]]I[[else]]We[[endif]] / [[if has_constitution]]Clause…[[else]]Paragraph 15…[[endif]]
const isSoleDirector = ref(false);  // 기존 이사 1명 = sole director (entity 에서 자동 감지)
const hasConstitution = computed(() => !!(articleNo.value || clauseNo.value)); // 정관(constitution) 보유 여부

// ─── 거버닝-문서 분기 (AOD/AGM 동일) — DCR resolution_basis_* ───
//   [[if resolution_basis_constitution]] / [[if resolution_basis_articles]] / [[if resolution_basis_third_schedule]]
const governingDocumentType = ref<GoverningDocumentType>('constitution');
// 기존 이사 수 — DCR [[if is_sole_director_company]] / [[if is_multiple_director_company]] 분기
const existingDirectorCount = ref<'single' | 'multiple'>('multiple');
// Entity 에 정관/결의 근거가 등록돼 있으면 true → 라디오 + clause 잠금(직원 오선택 방지).
const entityClauseRegistered = ref(false);
// Governing Document 편집 잠금 — Entity 등록값이 있으면 readonly. (articleNo 는 항상 entity 출처)
const governingDocLocked = computed(
  () => entityClauseRegistered.value || !!String(articleNo.value || '').trim(),
);

/** "Clause 59 of the Constitution" / "Article 15(d)" / "59" → 식별자(번호)만 추출. */
function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

// clause 콤보박스 옵션(entity 등록 clause 마스터). AOD/AnnualReturn 동일 패턴.
const articleNoOptions = ref<string[]>([]);
/** clause 옵션 문자열 분류 — Third Schedule > Articles(Article/Regulation/Table A/LLP) > Constitution(Clause). */
function clauseOptionType(opt: string): GoverningDocumentType | 'other' {
  const s = String(opt || '');
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
/** 선택된 Governing Document 라디오에 맞는 옵션만 노출(분류불가 'other'는 유지). */
const filteredClauseOptions = computed<string[]>(() =>
  articleNoOptions.value.filter((o) => {
    const ot = clauseOptionType(o);
    return ot === governingDocumentType.value || ot === 'other';
  })
);
/** 콤보박스 v-model — 선택/입력 시 parseClauseNo 로 번호만 저장 + 텍스트에서 타입 감지(setter). */
const clauseModel = computed<string>({
  get: () => clauseNo.value,
  set: (val: string | null) => {
    const s = val || '';
    clauseNo.value = parseClauseNo(s);
    const t = clauseOptionType(s);
    if (t !== 'other') governingDocumentType.value = t;
  },
});

/** Entity 등록 Governing Document 텍스트 → 라디오 타입 + clause 번호 적용 + 잠금 플래그 set.
 *  우선순위: Third Schedule > Constitution > Articles. (AOD applyRegisteredClause 와 동일) */
function applyRegisteredClause(regClause: string): boolean {
  const s = String(regClause || '').trim();
  if (!s) return false;
  entityClauseRegistered.value = true;
  if (/third\s*schedule/i.test(s)) {
    governingDocumentType.value = 'third_schedule';
    clauseNo.value = '';
  } else if (/constitution/i.test(s)) {
    governingDocumentType.value = 'constitution';
    clauseNo.value = parseClauseNo(s);
  } else if (/articles?\s+of\s+association/i.test(s)) {
    governingDocumentType.value = 'articles';
    clauseNo.value = parseClauseNo(s);
  } else {
    governingDocumentType.value = 'constitution';
    clauseNo.value = parseClauseNo(s);
  }
  return true;
}

// 라디오를 Third Schedule 로 바꾸면 clause 자동 비움 (번호 없음).
watch(governingDocumentType, (t) => {
  if (t === 'third_schedule') clauseNo.value = '';
});

// ─── 날짜 입력 (Section 46(3) 변경일자 / Declaration / Resolution 일자) ───
//   직접 입력 + 초록 달력 + X(skip) 모두 지원 (PreSetDateField). skip 된 날짜는 blank 로 lodge (AOD 와 동일).
const dateOfChange = ref('');
const dateOfDeclaration = ref('');
const dateOfResolution = ref('');

// 날짜 X(skip) 상태 — AOD 의 skippedFields 패턴.
const skippedFields = ref<Set<string>>(new Set());
function isFieldSkipped(key: string): boolean {
  return skippedFields.value.has(key);
}
function toggleFieldSkip(key: string, skipped: boolean) {
  const next = new Set(skippedFields.value);
  if (skipped) next.add(key);
  else next.delete(key);
  skippedFields.value = next;
}

// ─── 신규 주소 입력 (T-DocAI Malaysia 주소 cascading 위젯 재사용) ───
interface LocOption { code: string; name: string }
interface CodeOption { TOTAL_CODE: string; CODE_NAME: string }

const newAddress = ref({
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  countryCode: MALAYSIA_CODE as string,
  stateCode: '' as string,
  cityCode: '' as string,
  postcodeCode: '' as string,
  foreignState: '',
  foreignCity: '',
  foreignPostcode: '',
});

const countryOptions = ref<CodeOption[]>([]);
const stateOptions = ref<LocOption[]>([]);
const cityOptions = ref<LocOption[]>([]);
const postcodeOptions = ref<LocOption[]>([]);

const loadingCountries = ref(false);
const loadingStates = ref(false);
const loadingCities = ref(false);
const loadingPostcodes = ref(false);

const isMalaysiaSelected = computed(() =>
  !newAddress.value.countryCode || newAddress.value.countryCode === MALAYSIA_CODE
);

const postcodeInput = ref<string | LocOption | null>(null);
const isReverseLookup = ref(false);
let postcodeSearchDebounce: ReturnType<typeof setTimeout> | null = null;

onMounted(async () => {
  // Task Config 조회 — processSteps 로 taskFlow 플래그 유도 (T-Sign/E-Filing 등)
  try {
    const taskCode = props.selection.task.TASK_CODE;
    if (taskCode) {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = cfg?.processSteps || [];
    }
  } catch (err) {
    console.warn('[PreSetChangeAddress] Task config fetch failed:', err);
  }

  loadingCountries.value = true;
  try {
    const res = await baseCodeApi.getCodesByMainCode('CM', 'B15') as any;
    const codes = res?.codes || res?.data?.codes || [];
    countryOptions.value = codes
      .map((c: any) => ({
        TOTAL_CODE: String(c.totalCode || c.TOTAL_CODE || '').trim(),
        CODE_NAME: String(c.defaultCodeName || c.DEFAULT_CODE_NAME || c.codeName || c.CODE_NAME || '').trim().toUpperCase(),
      }))
      .filter((c: any) => c.TOTAL_CODE && c.CODE_NAME && !c.TOTAL_CODE.endsWith('000'));
  } catch (err) {
    console.error('[PreSetChangeAddress] Failed to load countries:', err);
  } finally {
    loadingCountries.value = false;
  }

  loadingStates.value = true;
  try {
    const res = await entityMyApi.getMalaysiaStates() as any;
    const data = res?.data?.data || res?.data || [];
    stateOptions.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('[PreSetChangeAddress] Failed to load states:', err);
    stateOptions.value = [];
  } finally {
    loadingStates.value = false;
  }
});

watch(() => newAddress.value.countryCode, (newCountry, oldCountry) => {
  if (newCountry === oldCountry) return;
  if (isReverseLookup.value) return; // 복원/역조회 중에는 자식 필드 리셋 금지
  newAddress.value.stateCode = '';
  newAddress.value.cityCode = '';
  newAddress.value.postcodeCode = '';
  newAddress.value.foreignState = '';
  newAddress.value.foreignCity = '';
  newAddress.value.foreignPostcode = '';
  cityOptions.value = [];
  postcodeOptions.value = [];
  postcodeInput.value = null;
});

watch(() => newAddress.value.stateCode, async (newState) => {
  if (isReverseLookup.value) return;
  newAddress.value.cityCode = '';
  newAddress.value.postcodeCode = '';
  postcodeInput.value = null;
  cityOptions.value = [];
  postcodeOptions.value = [];
  if (!newState) return;
  loadingCities.value = true;
  try {
    const res = await entityMyApi.getMalaysiaCities(newState) as any;
    const data = res?.data?.data || res?.data || [];
    cityOptions.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('[PreSetChangeAddress] Failed to load cities:', err);
    cityOptions.value = [];
  } finally {
    loadingCities.value = false;
  }
});

watch(() => newAddress.value.cityCode, async (newCity) => {
  if (isReverseLookup.value) return;
  newAddress.value.postcodeCode = '';
  postcodeInput.value = null;
  postcodeOptions.value = [];
  if (!newCity) return;
  loadingPostcodes.value = true;
  try {
    const res = await entityMyApi.getMalaysiaPostcodes(newCity) as any;
    const data = res?.data?.data || res?.data || [];
    postcodeOptions.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('[PreSetChangeAddress] Failed to load postcodes:', err);
    postcodeOptions.value = [];
  } finally {
    loadingPostcodes.value = false;
  }
});

watch(postcodeInput, (val) => {
  if (val && typeof val === 'object' && 'code' in val) {
    newAddress.value.postcodeCode = val.code;
  } else if (typeof val === 'string') {
    newAddress.value.postcodeCode = val;
  } else {
    newAddress.value.postcodeCode = '';
  }
});

function onPostcodeSearch(searchText: string) {
  if (postcodeSearchDebounce) clearTimeout(postcodeSearchDebounce);
  if (!searchText || searchText.length < 4) return;
  postcodeSearchDebounce = setTimeout(async () => {
    if (newAddress.value.stateCode && newAddress.value.cityCode) return;
    try {
      loadingPostcodes.value = true;
      const res = await entityMyApi.getMalaysiaPostcodesByNumber(searchText) as any;
      const data = res?.data?.data || res?.data || [];
      if (!Array.isArray(data) || data.length === 0) return;
      const match = data[0];
      const stateCode = match.state?.code;
      const cityCode = match.city?.code;
      if (!stateCode) return;
      isReverseLookup.value = true;
      if (newAddress.value.countryCode !== MALAYSIA_CODE) {
        newAddress.value.countryCode = MALAYSIA_CODE;
      }
      newAddress.value.stateCode = stateCode;
      const cityRes = await entityMyApi.getMalaysiaCities(stateCode) as any;
      cityOptions.value = Array.isArray(cityRes?.data?.data || cityRes?.data) ? (cityRes?.data?.data || cityRes?.data) : [];
      if (cityCode) {
        newAddress.value.cityCode = cityCode;
        const pcRes = await entityMyApi.getMalaysiaPostcodes(cityCode) as any;
        postcodeOptions.value = Array.isArray(pcRes?.data?.data || pcRes?.data) ? (pcRes?.data?.data || pcRes?.data) : [];
      }
      const pcOption = postcodeOptions.value.find(o => o.name === searchText || o.code === searchText);
      if (pcOption) {
        postcodeInput.value = pcOption.code;
        newAddress.value.postcodeCode = pcOption.code;
      } else {
        newAddress.value.postcodeCode = searchText;
      }
      isReverseLookup.value = false;
    } catch (err) {
      console.error('[PreSetChangeAddress] Postcode reverse lookup failed:', err);
      isReverseLookup.value = false;
    } finally {
      loadingPostcodes.value = false;
    }
  }, 500);
}

const countryName = computed(() =>
  countryOptions.value.find(o => o.TOTAL_CODE === newAddress.value.countryCode)?.CODE_NAME || ''
);
const stateName = computed(() => {
  if (isMalaysiaSelected.value) return stateOptions.value.find(o => o.code === newAddress.value.stateCode)?.name || '';
  return newAddress.value.foreignState.trim();
});
const cityName = computed(() => {
  if (isMalaysiaSelected.value) return cityOptions.value.find(o => o.code === newAddress.value.cityCode)?.name || '';
  return newAddress.value.foreignCity.trim();
});
const postcodeName = computed(() => {
  if (isMalaysiaSelected.value) {
    const found = postcodeOptions.value.find(o => o.code === newAddress.value.postcodeCode);
    return found?.name || newAddress.value.postcodeCode || '';
  }
  return newAddress.value.foreignPostcode.trim();
});

const entityName = computed(() => {
  const name = props.entityData?.companyName || props.entityData?.entityName || '';
  const code = props.entityData?.companySuffix || '';
  const suffixMap: Record<string, string> = {
    M31001: 'SDN. BHD.', M31002: 'BHD.', M31003: 'LTD.', M31004: 'PTE. LTD.',
  };
  const suffix = suffixMap[code] || code;
  return suffix ? `${name} ${suffix}` : name;
});
const icpCd = computed(() => props.entityData?.icpCd || props.entityData?.entityCd || '');

const newAddressComposed = computed(() => {
  const a = newAddress.value;
  const parts: string[] = [];
  if (a.addressLine1.trim()) parts.push(a.addressLine1.trim());
  if (a.addressLine2.trim()) parts.push(a.addressLine2.trim());
  if (a.addressLine3.trim()) parts.push(a.addressLine3.trim());
  const pc = postcodeName.value;
  const ct = cityName.value;
  if (pc && ct) parts.push(`${pc} ${ct}`);
  else if (ct) parts.push(ct);
  else if (pc) parts.push(pc);
  if (stateName.value) parts.push(stateName.value);
  const cn = countryName.value || (isMalaysiaSelected.value ? 'MALAYSIA' : '');
  if (cn) parts.push(cn);
  return parts.join(', ');
});

const isNewAddressValid = computed(() => {
  const a = newAddress.value;
  if (!a.addressLine1.trim()) return false;
  if (isMalaysiaSelected.value) {
    return !!(a.stateCode && a.cityCode && a.postcodeCode);
  }
  return !!(a.foreignState.trim() && a.foreignCity.trim() && a.foreignPostcode.trim());
});

// Entity 선택 시 주소 자동 조회 (L_ENTITY_PROFILE 5개 전용 컬럼 기반)
watch(
  () => icpCd.value,
  async (code) => {
    if (!code) return;
    addressInfoLoading.value = true;
    try {
      const entityAddr = await fetchEntityAddresses(code);
      if (entityAddr) {
        regNo.value = entityAddr.regNo || '';
        articleNo.value = entityAddr.articleNo || '';
        oldAddressComposed.value =
          entityAddr.registered || entityAddr.registeredOfficeAddress?.composed || '';
        oldAddressStructured.value = entityAddr.registeredOfficeAddress?.structured || {};
        // 거버닝-문서(결의 근거) 를 Entity 등록 clause/article 텍스트로 자동 판정 + 잠금 (AOD 와 동일).
        //   clauseNo 가 비어도 articleNo(전체 텍스트)로 fallback — 둘 다 ARTICLE_NO 기반.
        const regClause = String(entityAddr.clauseNo || entityAddr.articleNo || '').trim();
        if (regClause) {
          applyRegisteredClause(regClause);
        } else {
          clauseNo.value = entityAddr.clauseNo || '';
          entityClauseRegistered.value = false;
        }
      }
      // 기존 이사 수 자동 감지 — DCR [[if is_sole_director_company]] / [[if is_multiple_director_company]] (AOD 와 동일).
      try {
        const dRes = await fetch(`/api/documents/variables/directors/${code}`, { credentials: 'include' });
        const dJson = await dRes.json();
        const dirs: any[] = dJson?.data?.directors || dJson?.directors || [];
        const single = Array.isArray(dirs) && dirs.length === 1;
        isSoleDirector.value = single;
        existingDirectorCount.value = single ? 'single' : 'multiple';
      } catch { /* director 수 조회 실패 — 기본값(복수) 유지 */ }
      // clause 콤보박스 옵션 — resolution-options 엔드포인트(메모리 캐시). AOD 동일.
      try {
        const optRes = await fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' });
        const optJson = await optRes.json();
        const opts =
          optJson?.articleNoOptions ||
          optJson?.data?.resolution?.articleNoOptions ||
          optJson?.resolution?.articleNoOptions ||
          [];
        articleNoOptions.value = Array.isArray(opts)
          ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
          : [];
      } catch { articleNoOptions.value = []; }
    } catch (err) {
      console.warn('[PreSetChangeAddress] address-info load failed:', err);
    } finally {
      addressInfoLoading.value = false;
    }
  },
  { immediate: true }
);

// ─── Edit 모드 상태 캡처/복원 (presetForm 스냅샷) ───
function gatherState(): Record<string, any> {
  return {
    newAddress: JSON.parse(JSON.stringify(newAddress.value || {})),
    postcodeInput:
      postcodeInput.value && typeof postcodeInput.value === 'object'
        ? JSON.parse(JSON.stringify(postcodeInput.value))
        : postcodeInput.value,
    dateOfChange: dateOfChange.value,
    dateOfDeclaration: dateOfDeclaration.value,
    dateOfResolution: dateOfResolution.value,
    skippedFields: Array.from(skippedFields.value),
    regNo: regNo.value,
    articleNo: articleNo.value,
    clauseNo: clauseNo.value,
    isSoleDirector: isSoleDirector.value,
    governingDocumentType: governingDocumentType.value,
    existingDirectorCount: existingDirectorCount.value,
    entityClauseRegistered: entityClauseRegistered.value,
    oldAddressComposed: oldAddressComposed.value,
    oldAddressStructured: JSON.parse(JSON.stringify(oldAddressStructured.value || {})),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.newAddress && typeof s.newAddress === 'object') {
    // MY 주소는 State→City→Postcode 캐스케이드라, 코드만 복원하면 부모 watch 가 자식을 지운다.
    //   → isReverseLookup 가드로 리셋을 막고, 옵션(cities/postcodes)을 직접 로드하면서 코드 유지.
    void restoreMalaysiaAddress(s.newAddress, s.postcodeInput);
  } else if (s.postcodeInput !== undefined) {
    postcodeInput.value = s.postcodeInput;
  }
  if (s.dateOfChange != null) dateOfChange.value = s.dateOfChange;
  if (s.dateOfDeclaration != null) dateOfDeclaration.value = s.dateOfDeclaration;
  if (s.dateOfResolution != null) dateOfResolution.value = s.dateOfResolution;
  if (Array.isArray(s.skippedFields)) skippedFields.value = new Set(s.skippedFields);
  if (s.regNo != null) regNo.value = s.regNo;
  if (s.articleNo != null) articleNo.value = s.articleNo;
  if (s.clauseNo != null) clauseNo.value = s.clauseNo;
  if (typeof s.isSoleDirector === 'boolean') isSoleDirector.value = s.isSoleDirector;
  if (s.governingDocumentType) governingDocumentType.value = s.governingDocumentType;
  if (s.existingDirectorCount) existingDirectorCount.value = s.existingDirectorCount;
  if (typeof s.entityClauseRegistered === 'boolean') entityClauseRegistered.value = s.entityClauseRegistered;
  // 복원된 articleNo 로 잠금/타입 재판정 (entity 등록값 우선).
  if (s.articleNo) applyRegisteredClause(String(s.articleNo));
  if (s.oldAddressComposed != null) oldAddressComposed.value = s.oldAddressComposed;
  if (s.oldAddressStructured && typeof s.oldAddressStructured === 'object') {
    oldAddressStructured.value = { ...s.oldAddressStructured };
  }
  // cascading watcher 가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

/**
 * 저장된 신주소(코드)를 복원하면서 City/Postcode 옵션을 직접 로드한다.
 *   캐스케이드 watch(state→city→postcode)가 자식 코드를 비우지 않도록 isReverseLookup 가드 사용.
 */
async function restoreMalaysiaAddress(saved: Record<string, any>, savedPostcodeInput: any) {
  const arr = (res: any): any[] => {
    const d = res?.data?.data || res?.data || [];
    return Array.isArray(d) ? d : [];
  };
  isReverseLookup.value = true;
  try {
    newAddress.value = { ...newAddress.value, ...saved };
    if (isMalaysiaSelected.value) {
      if (saved.stateCode) {
        cityOptions.value = arr(await entityMyApi.getMalaysiaCities(saved.stateCode));
      }
      if (saved.cityCode) {
        postcodeOptions.value = arr(await entityMyApi.getMalaysiaPostcodes(saved.cityCode));
      }
      // 코드가 위 옵션 로드 await 사이에 watch 로 비워졌을 수 있으니 재확정.
      if (saved.cityCode != null) newAddress.value.cityCode = saved.cityCode;
      if (saved.postcodeCode != null) newAddress.value.postcodeCode = saved.postcodeCode;
    }
    // Postcode 입력은 postcodeInput 모델에 바인딩됨 — 저장값(또는 코드)로 설정.
    if (savedPostcodeInput !== undefined && savedPostcodeInput !== null) {
      postcodeInput.value = savedPostcodeInput;
    } else if (saved.postcodeCode) {
      postcodeInput.value = saved.postcodeCode;
    }
  } catch (err) {
    console.warn('[PreSetChangeAddress] restoreMalaysiaAddress failed:', err);
  } finally {
    isReverseLookup.value = false;
    nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
  }
}

defineExpose({ gatherState, restoreState });

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) {
    progress.value = 'Error: Task Title is required — please enter it in the chat.';
    return;
  }
  if (!props.entityData) {
    progress.value = 'Error: Please select an entity in the chat first.';
    return;
  }
  if (!isNewAddressValid.value) {
    progress.value = 'Error: Please fill required new address fields (Line 1, City, State, Postcode).';
    return;
  }

  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task Code not configured in PreSet.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    const steps = (taskConfigSteps.value || []).map(s => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };

    // Change Address 전용 mapper 로 템플릿 변수 생성 (snake_case + alias 모두 포함)
    const commonFormData = buildChangeAddressVariables({
      companyName: entityName.value,
      registrationNo: regNo.value,
      oldAddressComposed: oldAddressComposed.value,
      oldAddressStructured: oldAddressStructured.value,
      newAddressComposed: newAddressComposed.value,
      newAddressStructured: {
        addressLine1: newAddress.value.addressLine1,
        addressLine2: newAddress.value.addressLine2,
        addressLine3: newAddress.value.addressLine3,
        country: countryName.value,
        state: stateName.value,
        city: cityName.value,
        postcode: postcodeName.value,
      },
      dateOfChange: dateOfChange.value,
      dateOfDeclaration: dateOfDeclaration.value,
      dateOfResolution: dateOfResolution.value,
      skippedDates: {
        dateOfChange: isFieldSkipped('dateOfChange'),
        dateOfDeclaration: isFieldSkipped('dateOfDeclaration'),
        dateOfResolution: isFieldSkipped('dateOfResolution'),
      },
      clauseNo: clauseNo.value,
      articleNo: articleNo.value,
      isSoleDirector: isSoleDirector.value,
      hasConstitution: hasConstitution.value,
      // ── 새 DCR 모델 (AOD/AGM 동일) ──
      governingDocumentType: governingDocumentType.value,
      directorCount: existingDirectorCount.value,
      constitutionClauseNumber: clauseNo.value,
    });

    // Task 생성 — TASK_DATA.formData 에 commonFormData (새 주소 정보) 동봉.
    //   Register 단계 detail 모달이 이 키들을 사용 (new_registered_address / new_address_* / ...).
    //   presetForm: Edit 모드 복원용 스냅샷.
    const officerData = { ...commonFormData, presetForm: gatherState() };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
      progress.value = `Updating documents (Task ${taskId})...`;
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: title,
          taskFlow,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
      progress.value = `Task created (ID: ${taskId}). Saving documents...`;
    }

    const docsToSave = (props.selection.documents || [])
      .filter(d => d.tmpId)
      .map(d => ({
        templateId: d.tmpId,
        label: d.templateName,
        personName: entityName.value || '',
        formData: { ...commonFormData },
        entityCd: icpCd.value || undefined,
      }));

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err: any) {
        console.warn('[PreSetChangeAddress] Document save failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch { /* ignore */ }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[PreSetChangeAddress] create task failed:', err);
    progress.value = `Error: ${err.message}`;
  } finally {
    creating.value = false;
  }
}
</script>

<template>
  <div class="ca-panel">
    <!-- 헤더 -->
    <div class="ca-header">
      <div>
        <div class="ca-title">{{ selection.task.TASK_NAME }}</div>
        <div class="ca-subtitle">Change of Registered Office Address</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>
        Back
      </v-btn>
    </div>

    <div class="ca-content">
      <!-- Task Title (chat 에서 입력된 값을 읽기전용으로 표시) -->
      <div class="ca-section">
        <div class="ca-section-header">
          <Icon icon="mdi:text-box-outline" width="16" color="#666" />
          <span>Task Title</span>
        </div>
        <div v-if="effectiveTaskTitle" class="d-flex align-start justify-space-between ga-3">
          <div class="ca-readonly-box flex-grow-1">
            {{ effectiveTaskTitle }}
          </div>
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            @click="emit('change-task-title')"
          >
            <v-icon start size="16">mdi-pencil</v-icon>
            Change
          </v-btn>
        </div>
        <div v-else class="ca-readonly-box ca-readonly-empty">
          Waiting for task title (enter it in the chat on the left)...
        </div>
      </div>

      <!-- Entity 카드 -->
      <div v-if="entityData" class="ca-entity-card">
        <div class="d-flex align-center ga-3 justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="ca-entity-icon">
              <Icon icon="mdi:domain" width="24" color="#1B84FF" />
            </div>
            <div>
              <div class="ca-entity-name">{{ entityName }}</div>
              <div v-if="regNo" class="ca-entity-reg">Registration No: {{ regNo }}</div>
              <div v-if="articleNo" class="ca-entity-reg" :title="articleNo">
                Constitution Clause: <strong>{{ clauseNo || '—' }}</strong>
                <span class="ca-entity-article">({{ articleNo }})</span>
              </div>
            </div>
          </div>
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            @click="emit('change-entity')"
          >
            <v-icon start size="16">mdi-swap-horizontal</v-icon>
            Change
          </v-btn>
        </div>
      </div>

      <div v-else class="ca-empty">
        <Icon icon="mdi:link-variant-plus" width="40" color="#d0d0d0" class="mb-2" />
        <div style="font-size: 14px; font-weight: 600; color: #666;">No entity linked</div>
        <div style="font-size: 12px; color: #999; margin-top: 4px;">
          Use the chatbot on the left to select a T-Link entity.
        </div>
      </div>

      <!-- 거버닝 문서 (DCR 결의 근거) — Constitution / Articles / Third Schedule -->
      <div v-if="entityData" class="ca-section">
        <div class="ca-section-header">
          <Icon icon="mdi:file-document-outline" width="16" color="#888" />
          <span>Governing Document (Resolution Basis)</span>
          <span v-if="governingDocLocked" class="ca-gov-locked">
            <v-icon size="13">mdi-lock-outline</v-icon> registered
          </span>
        </div>
        <v-radio-group
          v-model="governingDocumentType"
          :disabled="governingDocLocked"
          density="compact"
          hide-details
          class="ca-gov-radio"
        >
          <v-radio label="Constitution" value="constitution" />
          <v-radio label="Articles of Association" value="articles" />
          <v-radio label="Third Schedule (Companies Act 2016)" value="third_schedule" />
        </v-radio-group>
        <!-- Third Schedule: 조항 없음 → N/A -->
        <v-text-field
          v-if="governingDocumentType === 'third_schedule'"
          model-value="N/A"
          disabled
          label="Constitution Clause No"
          density="compact"
          variant="outlined"
          hide-details="auto"
          class="mt-2"
        />
        <!-- Entity 등록(잠금): readonly 텍스트 -->
        <v-text-field
          v-else-if="governingDocLocked"
          v-model="clauseNo"
          :label="(governingDocumentType === 'articles' ? 'Article No' : 'Constitution Clause No') + ' (registered)'"
          readonly
          append-inner-icon="mdi-lock"
          density="compact"
          variant="outlined"
          hide-details="auto"
          class="mt-2"
        />
        <!-- 미등록: 콤보박스(직접입력 + 옵션 선택) — 라디오 타입에 맞는 옵션만 필터 -->
        <v-combobox
          v-else
          v-model="clauseModel"
          :items="filteredClauseOptions"
          :label="governingDocumentType === 'articles' ? 'Article No' : 'Constitution Clause No'"
          :placeholder="governingDocumentType === 'articles' ? 'Select or type article number' : 'Select or type clause number'"
          density="compact"
          variant="outlined"
          hide-details="auto"
          clearable
          class="mt-2"
        />
        <div class="ca-gov-hint">
          {{
            governingDocumentType === 'third_schedule'
              ? 'Paragraph 15 of the Third Schedule of the Companies Act 2016'
              : governingDocumentType === 'articles'
                ? (clauseNo ? `Article ${clauseNo} of the Company's Constitution (Articles of Association)` : "the Company's Constitution (Articles of Association)")
                : (clauseNo ? `Clause ${clauseNo} of the Company's Constitution` : "the Company's Constitution")
          }}
        </div>
      </div>

      <!-- 구 등기 주소 (읽기 전용) -->
      <div v-if="entityData" class="ca-section">
        <div class="ca-section-header">
          <Icon icon="mdi:map-marker-outline" width="16" color="#888" />
          <span>Old Registered Office Address</span>
        </div>
        <div v-if="addressInfoLoading" class="ca-old-addr ca-old-addr-loading">
          <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
          Loading...
        </div>
        <div v-else-if="oldAddressComposed" class="ca-old-addr">
          {{ oldAddressComposed }}
        </div>
        <div v-else class="ca-old-addr ca-old-addr-empty">
          No registered address on file.
        </div>
      </div>

      <!-- 신규 주소 입력 (T-DocAI Malaysia cascading 위젯 스타일) -->
      <div v-if="entityData" class="ca-section">
        <div class="ca-section-header">
          <Icon icon="mdi:map-marker-plus-outline" width="16" color="#1B84FF" />
          <span>New Registered Office Address</span>
        </div>

        <v-text-field
          v-model="newAddress.addressLine1"
          label="Address Line 1 *"
          variant="outlined" density="compact" hide-details
        />
        <v-text-field
          v-model="newAddress.addressLine2"
          label="Address Line 2"
          variant="outlined" density="compact" hide-details
          class="mt-2"
        />
        <v-text-field
          v-model="newAddress.addressLine3"
          label="Address Line 3"
          variant="outlined" density="compact" hide-details
          class="mt-2"
        />

        <v-row dense class="mt-2">
          <v-col cols="6">
            <v-combobox
              v-if="isMalaysiaSelected"
              v-model="postcodeInput"
              :items="postcodeOptions"
              item-title="name"
              item-value="code"
              label="Postcode *"
              placeholder="Type or select..."
              variant="outlined" density="compact" hide-details="auto"
              clearable auto-select-first
              :loading="loadingPostcodes"
              :return-object="false"
              @update:search="onPostcodeSearch"
            />
            <v-text-field
              v-else
              v-model="newAddress.foreignPostcode"
              label="Postcode *"
              variant="outlined" density="compact" hide-details
            />
          </v-col>
          <v-col cols="6">
            <v-autocomplete
              v-if="isMalaysiaSelected"
              v-model="newAddress.cityCode"
              :items="cityOptions"
              item-title="name"
              item-value="code"
              label="City *"
              placeholder="Search..."
              variant="outlined" density="compact" hide-details="auto"
              clearable auto-select-first
              :loading="loadingCities"
              :disabled="!newAddress.stateCode"
            />
            <v-text-field
              v-else
              v-model="newAddress.foreignCity"
              label="City *"
              variant="outlined" density="compact" hide-details
            />
          </v-col>
        </v-row>

        <v-row dense class="mt-2">
          <v-col cols="6">
            <v-autocomplete
              v-if="isMalaysiaSelected"
              v-model="newAddress.stateCode"
              :items="stateOptions"
              item-title="name"
              item-value="code"
              label="State *"
              placeholder="Search..."
              variant="outlined" density="compact" hide-details="auto"
              clearable auto-select-first
              :loading="loadingStates"
            />
            <v-text-field
              v-else
              v-model="newAddress.foreignState"
              label="State *"
              variant="outlined" density="compact" hide-details
            />
          </v-col>
        </v-row>

        <v-autocomplete
          v-model="newAddress.countryCode"
          :items="countryOptions"
          item-title="CODE_NAME"
          item-value="TOTAL_CODE"
          label="Country"
          placeholder="Search..."
          variant="outlined" density="compact" hide-details="auto"
          clearable auto-select-first
          :loading="loadingCountries"
          class="mt-2"
        />
      </div>

      <!-- 날짜 -->
      <div v-if="entityData" class="ca-section">
        <div class="ca-section-header">
          <Icon icon="mdi:calendar-outline" width="16" color="#888" />
          <span>Dates</span>
        </div>
        <v-row dense>
          <v-col cols="6">
            <PreSetDateField
              v-model="dateOfChange"
              label="Date of Change of Address"
              skippable
              :skipped="isFieldSkipped('dateOfChange')"
              @update:skipped="(v) => toggleFieldSkip('dateOfChange', v)"
            />
          </v-col>
          <v-col cols="6">
            <PreSetDateField
              v-model="dateOfDeclaration"
              label="Date of Declaration"
              skippable
              :skipped="isFieldSkipped('dateOfDeclaration')"
              @update:skipped="(v) => toggleFieldSkip('dateOfDeclaration', v)"
            />
            <div v-if="!isFieldSkipped('dateOfDeclaration')" class="ca-date-hint">Defaults to Date of Change of Address</div>
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="6">
            <PreSetDateField
              v-model="dateOfResolution"
              label="Date of Resolution"
              skippable
              :skipped="isFieldSkipped('dateOfResolution')"
              @update:skipped="(v) => toggleFieldSkip('dateOfResolution', v)"
            />
            <div v-if="!isFieldSkipped('dateOfResolution')" class="ca-date-hint">Defaults to Date of Declaration</div>
          </v-col>
        </v-row>
      </div>

      <!-- 문서 목록 -->
      <div class="ca-section">
        <div class="ca-section-header">
          <Icon icon="mdi:file-document-multiple-outline" width="16" color="#5c6bc0" />
          <span>Documents ({{ selection.documents.length }})</span>
        </div>
        <div
          v-for="(doc, idx) in selection.documents"
          :key="doc.docCode"
          class="ca-doc-item"
        >
          <div class="ca-doc-num">{{ idx + 1 }}</div>
          <Icon icon="mdi:file-document-outline" width="16" color="#5c6bc0" />
          <div>
            <div style="font-size: 12px; font-weight: 500;">{{ doc.templateName }}</div>
            <div style="font-size: 10px; color: #999;">{{ doc.docCode }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 프로그레스 -->
    <div v-if="creating || progress" class="ca-progress">
      <v-progress-linear v-if="creating" indeterminate color="primary" height="3" />
      <div class="ca-progress-text" :class="{ 'text-error': progress.startsWith('Error') }">
        {{ progress }}
      </div>
    </div>

    <!-- 하단 버튼 -->
    <div class="ca-footer">
      <v-btn variant="outlined" color="grey" size="small" @click="emit('back')">
        <Icon icon="mdi:arrow-left" width="16" class="mr-1" />
        Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :disabled="creating || (editMode && !isDirty)"
              :loading="creating"
              @click="handleCreateTask"
            >
              <Icon :icon="editMode ? 'mdi:content-save' : 'mdi:check'" width="16" class="mr-1" />
              {{ editMode ? 'Update Task' : 'Create Task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<style scoped>
.ca-date-hint { font-size: 12px; color: rgba(0, 0, 0, 0.6); margin-top: 2px; padding-left: 4px; }
.ca-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ca-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.ca-title { font-size: 18px; font-weight: 700; color: #1a1a2e; }
.ca-subtitle { font-size: 12px; color: #888; margin-top: 2px; }
.ca-content { flex: 1; overflow-y: auto; padding: 20px 24px; }
.ca-entity-card {
  background: #f0f5ff; border: 1px solid #d6e4ff; border-radius: 12px;
  padding: 16px; margin-bottom: 20px;
}
.ca-entity-icon {
  width: 40px; height: 40px; border-radius: 10px; background: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.ca-entity-name { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.ca-entity-reg { font-size: 12px; color: #666; margin-top: 1px; }
.ca-entity-article {
  color: #999; margin-left: 4px; font-size: 11px;
  display: inline-block; max-width: 280px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  vertical-align: middle;
}
.ca-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 28px 16px; background: #fafafa; border: 1px dashed #e0e0e0; border-radius: 12px;
  margin-bottom: 20px; text-align: center;
}
.ca-section { margin-bottom: 16px; }
.ca-section-header {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: #555;
  margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #f0f0f0;
}
.ca-readonly-box {
  background: #fafafa; border: 1px solid #eee; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; color: #333; line-height: 1.5;
}
.ca-readonly-empty { color: #bbb; font-style: italic; }
.ca-old-addr {
  background: #fafafa; border: 1px solid #eee; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; color: #333; line-height: 1.5;
}
.ca-old-addr-loading { display: flex; align-items: center; color: #888; }
.ca-old-addr-empty { color: #bbb; font-style: italic; }
.ca-gov-locked {
  display: inline-flex; align-items: center; gap: 2px;
  margin-left: auto; font-size: 11px; font-weight: 600; color: #888;
}
.ca-gov-radio { margin-top: 0; }
.ca-gov-hint {
  margin-top: 6px; font-size: 12px; color: #1B84FF;
  background: #f5f9ff; border: 1px solid #e3f0ff; border-radius: 6px;
  padding: 6px 10px; line-height: 1.4;
}
.ca-new-addr-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.ca-new-addr-grid .grid-col-2 { grid-column: span 2; }
@media (max-width: 700px) {
  .ca-new-addr-grid { grid-template-columns: 1fr; }
  .ca-new-addr-grid .grid-col-2 { grid-column: span 1; }
}
.ca-doc-item { display: flex; align-items: center; gap: 8px; padding: 6px 0; }
.ca-doc-num {
  width: 20px; height: 20px; border-radius: 4px;
  background: #eef2ff; color: #5c6bc0; font-size: 10px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ca-progress { padding: 0 24px 8px; }
.ca-progress-text { font-size: 11px; color: #888; margin-top: 4px; text-align: center; }
.ca-footer {
  display: flex; justify-content: space-between;
  padding: 12px 24px; border-top: 1px solid #f0f0f0;
}
</style>
